/**
 * Accessible error feedback for natively-validated forms (WCAG 3.3.1 Error
 * Identification, 3.3.3 Error Suggestion, 4.1.3 Status Messages).
 *
 * Generic by design: it works for ANY <form> that relies on the browser's
 * built-in constraint validation — the HTML Forms contact form, the team-member
 * finder, and any future form — because it keys off each control's own
 * `validity`/`validationMessage`, never hard-coded field names or form ids.
 *
 * Forms that run their own (already accessible) validation opt out via the
 * standard `novalidate` attribute: Contact Form 7 adds `novalidate` and ships
 * its own aria-invalid / error-tip / response-output handling, and the search
 * box is skipped explicitly. So nothing here fights another validator.
 *
 * For every control that fails validation it:
 *   - suppresses the browser's transient default bubble,
 *   - sets `aria-invalid="true"`,
 *   - renders a persistent visible error message linked via `aria-describedby`
 *     (so landing on the field announces its error), and
 *   - once the whole validation pass is done, moves focus to the first invalid
 *     control and mirrors a summary into a live region so it is spoken.
 * Each error clears the moment its field becomes valid again.
 */
(function () {
  "use strict";

  var FORM_SELECTOR = "form:not([novalidate])";
  var SKIP_SELECTOR = ".search-form";
  var ERROR_PREFIX = "form-a11y-error-";
  var FLAG = "__debA11yValidate";

  function esc(id) {
    return window.CSS && CSS.escape ? CSS.escape(id) : id;
  }

  // Error text in the current SITE language (localized server-side into
  // window.deacTheme.formA11y), chosen by which constraint failed. Falls back
  // to the browser's own (browser-locale) message if the strings are absent.
  function messageFor(field) {
    var strings = (window.deacTheme && window.deacTheme.formA11y) || null;
    var v = field.validity;
    if (strings) {
      if (v.valueMissing && strings.required) {
        return strings.required;
      }
      if (v.typeMismatch && field.type === "email" && strings.email) {
        return strings.email;
      }
      if (strings.default) {
        return strings.default;
      }
    }
    return field.validationMessage;
  }

  // Accessible name of a control: <label for>, wrapping <label>, aria-label,
  // or placeholder — whichever exists. The trailing required "*" is stripped.
  function fieldLabel(field) {
    var id = field.id;
    if (id) {
      var l = document.querySelector('label[for="' + esc(id) + '"]');
      if (l) {
        return l.textContent.replace(/\s+/g, " ").replace(/\*\s*$/, "").trim();
      }
    }
    var wrap = field.closest ? field.closest("label") : null;
    if (wrap) {
      return wrap.textContent.replace(/\s+/g, " ").replace(/\*\s*$/, "").trim();
    }
    return (field.getAttribute("aria-label") || field.getAttribute("placeholder") || "").trim();
  }

  function liveRegion(form) {
    var live = form.querySelector(".js-form-a11y-live");
    if (!live) {
      live = document.createElement("div");
      live.className = "js-form-a11y-live screen-reader-text";
      live.setAttribute("aria-live", "assertive");
      live.setAttribute("aria-atomic", "true");
      form.insertBefore(live, form.firstChild);
    }
    return live;
  }

  function errorIdFor(field, index) {
    var base = field.id || field.getAttribute("name") || "field";
    return ERROR_PREFIX + base.replace(/[^a-zA-Z0-9_-]/g, "") + "-" + index;
  }

  function addDescribedBy(field, id) {
    var ids = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
    if (ids.indexOf(id) === -1) {
      ids.push(id);
      field.setAttribute("aria-describedby", ids.join(" "));
    }
  }

  function showError(field, index) {
    field.setAttribute("aria-invalid", "true");
    var id = errorIdFor(field, index);
    var el = document.getElementById(id);
    if (!el) {
      el = document.createElement("span");
      el.id = id;
      el.className = "form-field-error";
      field.parentNode.insertBefore(el, field.nextSibling);
      addDescribedBy(field, id);
    }
    var message = messageFor(field);
    el.textContent = message;
    return { label: fieldLabel(field), message: message };
  }

  function clearError(field) {
    field.setAttribute("aria-invalid", "false");
    var ids = (field.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean);
    var kept = [];
    ids.forEach(function (id) {
      if (id.indexOf(ERROR_PREFIX) === 0) {
        var el = document.getElementById(id);
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      } else {
        kept.push(id);
      }
    });
    if (kept.length) {
      field.setAttribute("aria-describedby", kept.join(" "));
    } else {
      field.removeAttribute("aria-describedby");
    }
  }

  function announce(form, items) {
    if (!items.length) {
      return;
    }
    var live = liveRegion(form);
    var summary = items
      .map(function (it) {
        return (it.label ? it.label + ": " : "") + it.message;
      })
      .join(". ");
    // Clear first so an identical re-submit is still announced as a change.
    live.textContent = "";
    window.setTimeout(function () {
      live.textContent = summary;
    }, 60);
    if (items[0].field && typeof items[0].field.focus === "function") {
      items[0].field.focus();
    }
  }

  function enhance(form) {
    if (form[FLAG] || form.matches(SKIP_SELECTOR)) {
      return;
    }
    form[FLAG] = true;

    // Make sure required controls are announced as required even if some other
    // pass missed them.
    form.querySelectorAll("[required]").forEach(function (f) {
      if (!f.hasAttribute("aria-required")) {
        f.setAttribute("aria-required", "true");
      }
    });

    // Create the (empty) live region up front: screen readers announce changes
    // far more reliably when the region already exists at page load than when
    // it is injected at the same moment its first message is added.
    liveRegion(form);

    var pending = [];
    var scheduled = false;

    // `invalid` does not bubble, so listen in the capture phase.
    form.addEventListener(
      "invalid",
      function (event) {
        var field = event.target;
        if (!field || !field.willValidate) {
          return;
        }
        event.preventDefault(); // hide the native bubble; we show our own
        var info = showError(field, pending.length);
        pending.push({ field: field, label: info.label, message: info.message });
        if (!scheduled) {
          scheduled = true;
          window.setTimeout(function () {
            announce(form, pending.slice());
            pending = [];
            scheduled = false;
          }, 0);
        }
      },
      true
    );

    function onFix(event) {
      var field = event.target;
      if (field && field.willValidate && field.checkValidity()) {
        clearError(field);
      }
    }
    form.addEventListener("input", onFix, true);
    form.addEventListener("change", onFix, true);
  }

  function init() {
    document.querySelectorAll(FORM_SELECTOR).forEach(enhance);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
